<script setup lang="ts">
import {computed} from "vue";
import {Check, FileDown, LoaderCircle} from "@lucide/vue";
import {Textarea} from "@/core/components/ui/textarea";
import {Button} from "@/core/components/ui/button";
import {Label} from "@/core/components/ui/label";
import {CARD_SHEET} from "@/medication-checkup/services/SceneClasses";
import MedicationCheckupBillingReminder from "@/medication-checkup/components/MedicationCheckupBillingReminder.vue";
import MedicationCheckupKnowledgeAppraisal from "@/medication-checkup/components/MedicationCheckupKnowledgeAppraisal.vue";
import {KnowledgeAppraisalAnswers, KnowledgeAppraisalView} from "@/medication-checkup/interfaces/knowledge-appraisal";

/**
 * The card of a closing that asks the patient's questions, as the
 * Assurance Maladie forms of the advice interview and of the follow-up do,
 * then what the conclusions form of the year asks of the pharmacist: the
 * success header, the questions, the synthesis of the interview, the
 * appraisal of what the patient knows, the acts to bill when the closing
 * serves some and the forms to download, shown from the start but offered
 * only once frozen. How the texts are
 * asked is the card's own; the words of the header and of the downloads
 * belong to each closing.
 *
 * Typing is local to the scene: the card shows the texts it is handed while
 * live, the ones the server froze once the block is completed. Downloading
 * is the scene's too: the card asks for a form and shows its wait.
 */

/**
 * A form the closing offers, as the scene hands it.
 */
export interface ClosingDownload {
    key: string;
    label: string;
    /**
     * Whether the HDS serves it: only once the block is completed.
     */
    available: boolean;
    downloading: boolean;
}

/**
 * The texts of the live scene, until the closing action submits them.
 */
export interface QuestionsClosingAnswers {
    questions: string;
    conclusion: string;
}

/**
 * What the server froze once the block is completed.
 */
export interface QuestionsClosingRecord extends KnowledgeAppraisalView {
    questions: string | null;
    conclusion: string | null;
}

const props = defineProps<{
    /**
     * The success header.
     */
    headline: string;
    recorded: QuestionsClosingRecord;
    /**
     * The acts to bill, one line each, when the closing serves some.
     */
    billing?: string[] | null;
    /**
     * The forms there are to download, offered once frozen.
     */
    downloads?: ClosingDownload[];
    frozen?: boolean;
    /**
     * The texts typed so far, while the closing is live.
     */
    answers?: QuestionsClosingAnswers;
    /**
     * The appraisal as it stands, while the closing is live.
     */
    appraisal?: KnowledgeAppraisalAnswers;
}>();

const emit = defineEmits<{
    typed: [field: keyof QuestionsClosingAnswers, value: string];
    appraised: [appraisal: KnowledgeAppraisalAnswers];
    download: [key: string];
}>();

/**
 * The two fields in order: how each is asked, and the height its text
 * deserves.
 */
const FIELDS: { key: keyof QuestionsClosingAnswers; label: string; placeholder: string; rows: number }[] = [
    {
        key: 'questions',
        label: 'Le patient a-t-il des questions ou des remarques ?',
        placeholder: 'Noter ici les questions / remarques du patient…',
        rows: 3,
    },
    {
        key: 'conclusion',
        label: "Synthèse de l'entretien",
        placeholder: 'Reportée sur le formulaire de synthèse…',
        rows: 3,
    },
];

/**
 * What a field reads as: the local text while live, the one the server
 * froze once the block is completed.
 */
const valueOf = (field: keyof QuestionsClosingAnswers): string => {
    return props.frozen ? props.recorded[field] ?? '' : props.answers?.[field] ?? '';
};

const typed = (field: keyof QuestionsClosingAnswers, value: string | number) => {
    emit('typed', field, String(value));
};

/**
 * Whether a form can be downloaded: the buttons wait, disabled, for the
 * validation.
 */
const served = (download: ClosingDownload): boolean => props.frozen && download.available;

/**
 * What the downloads read beside them: when they will be offered, then that
 * they stay at hand.
 */
const downloadsHint = computed<string>(() => {
    if (!props.downloads?.every(served)) {
        return 'Disponible après validation';
    }

    return props.downloads.length > 1
        ? 'Vous pourrez les retrouver plus tard'
        : 'Vous pourrez le retrouver plus tard';
});
</script>

<template>
  <div
    :class="CARD_SHEET"
    class="max-w-[640px] rounded-xl overflow-hidden"
  >
    <div class="flex items-center gap-3 px-4 py-3.5 bg-secondary-lightest text-secondary-darkest">
      <span class="w-9 h-9 shrink-0 rounded-full bg-white flex items-center justify-center">
        <Check class="w-5 h-5 text-secondary-darkest" :stroke-width="3" />
      </span>
      <span class="text-base font-bold">{{ headline }}</span>
    </div>

    <div class="flex flex-col gap-4 px-4 py-4">
      <div
        v-for="field in FIELDS"
        :key="field.key"
        class="flex flex-col gap-1.5"
      >
        <Label class="text-sm font-semibold">{{ field.label }}</Label>
        <Textarea
          v-if="!frozen || recorded[field.key]"
          :aria-label="field.label"
          :model-value="valueOf(field.key)"
          :placeholder="field.placeholder"
          :rows="field.rows"
          :disabled="frozen"
          class="min-h-0 rounded-lg bg-white shadow-none resize-none focus-visible:ring-0 focus-visible:border-primary disabled:opacity-100 disabled:text-gray-dark"
          @update:model-value="typed(field.key, $event)"
        />
        <span v-else class="text-sm italic text-muted-foreground">Non renseigné</span>
      </div>

      <MedicationCheckupKnowledgeAppraisal
        :recorded="recorded"
        :frozen="frozen"
        :appraisal="appraisal"
        @appraised="emit('appraised', $event)"
      />
    </div>

    <MedicationCheckupBillingReminder
      :lines="billing"
      class="border-t border-gray-light"
    />

    <div
      v-if="downloads?.length"
      class="flex flex-wrap gap-2 items-center px-4 py-3 border-t border-gray-light"
    >
      <Button
        v-for="download in downloads"
        :key="download.key"
        variant="secondary"
        class="inline-flex items-center gap-1.5 text-sm disabled:opacity-60 shrink-0"
        :disabled="!served(download) || download.downloading"
        @click="emit('download', download.key)"
      >
        <LoaderCircle v-if="download.downloading" class="animate-spin w-3.5 h-3.5" />
        <FileDown v-else class="w-3.5 h-3.5" />
        {{ download.label }}
      </Button>
      <span class="text-sm text-muted-foreground">{{ downloadsHint }}</span>
    </div>
  </div>
</template>
